import { useGetUserByIdQuery } from '@/services/api/tenant/userEnhanced'
import * as amplitude from '@amplitude/analytics-browser'
import { skipToken } from '@reduxjs/toolkit/query/react'

import i18next from 'i18next'
import { useEffect } from 'react'
import { FullScreenProgress } from '../../../../common/components/FullScreenProgress'
import { RouteResponse } from '../../../../core/router'
import { LoggedOutModal, PrivateRoute, UserData, authSlice, useAuthUser } from '../../../../features/auth'
import { useTypedDispatch } from '../../../../store'
import { AuthenticatedRoute } from '../AuthenticatedRoute/AuthenticatedRoute'

type UserMiddlewareProps = {
  route: RouteResponse | undefined
}

export const UserMiddleware = ({ route }: UserMiddlewareProps): JSX.Element => {
  const dispatch = useTypedDispatch()
  const user = useAuthUser()

  let queryArg: typeof skipToken | { tenantId: string; userId: string }

  if (!user || !user.tenant_id || !user.user_id) {
    queryArg = skipToken
  } else {
    queryArg = {
      tenantId: user.tenant_id,
      userId: user.user_id,
    }
  }

  const { data: userLanguage } = useGetUserByIdQuery(queryArg)

  useEffect(() => {
    const changeLanguage = async () => {
      if (userLanguage?.language_tag && ['en-GB', 'de-DE'].includes(userLanguage.language_tag)) {
        const languageToSet = userLanguage.language_tag
        await i18next.changeLanguage(languageToSet)
      }
    }
    changeLanguage()
  }, [userLanguage?.language_tag])

  const setAmplitudeUser = (user: UserData) => {
    amplitude.setUserId(user.user_id)

    const identifyEvent = new amplitude.Identify()
    identifyEvent.setOnce('first_login', new Date().toISOString())
    identifyEvent.set('user_role', user.roles[0])
    amplitude.identify(identifyEvent)
  }

  useEffect(() => {
    if (user) {
      setAmplitudeUser(user)
      dispatch(authSlice.actions.setCurrentUser(user))
    }
  }, [user, dispatch])

  if (!user) {
    return (
      <>
        <FullScreenProgress />
        <LoggedOutModal />
      </>
    )
  }

  return route && user.roles ? (
    <PrivateRoute route={route} tenantId={user.tenant_id} roles={user.roles} />
  ) : (
    <AuthenticatedRoute />
  )
}
